import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Coins, Crown, Check, Copy, X, Gift, History, Ticket } from 'lucide-react';
import api from '../../lib/api';

export const PointsMarket = ({ benefits, myPoints, isSubscriber, isAuthenticated, redemptions, onRefresh }) => {
  const [redeeming, setRedeeming] = useState(null);
  const [newCode, setNewCode] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);

  const redeem = async (b) => {
    if (!isAuthenticated) return toast.info('Iniciá sesión para canjear tus puntos');
    if (!window.confirm(`¿Canjear "${b.title}" por ${b.points_cost.toLocaleString()} puntos?`)) return;
    setRedeeming(b.id);
    try {
      const r = await api.post(`/fullbravx/benefits/${b.id}/redeem`);
      setNewCode(r.data.redemption);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo canjear');
    } finally { setRedeeming(null); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success('Código copiado');
  };

  return (
    <div data-testid="points-market">
      {/* My redemptions toggle */}
      {isAuthenticated && redemptions.length > 0 && (
        <div className="mb-6">
          <button onClick={() => setShowHistory(!showHistory)} className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white uppercase tracking-widest font-bold" data-testid="toggle-my-redemptions">
            <History size={13} className="text-[#D4AF37]" /> Mis canjes ({redemptions.length})
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {redemptions.map(r => (
                <div key={r.id} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-white text-sm font-semibold flex items-center gap-2">
                      {r.benefit_title}
                      {r.status === 'used' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-700/70 text-zinc-300 uppercase font-bold">Usado</span>}
                    </p>
                    <p className="text-white/40 text-[11px]">{new Date(r.created_at).toLocaleDateString('es-AR')} · {r.points_spent} pts</p>
                  </div>
                  <button onClick={() => copy(r.code)} className={`font-mono font-black tracking-widest text-sm flex items-center gap-1.5 ${r.status === 'used' ? 'text-white/25 line-through' : 'text-[#D4AF37] hover:text-[#F2D272]'}`}>
                    {r.code} <Copy size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {benefits.length === 0 ? (
        <p className="text-white/40 text-sm italic">Pronto vas a poder canjear tus puntos por beneficios. ¡Seguí sumando!</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map((b, idx) => {
            const affordable = myPoints >= b.points_cost;
            const blockedBySub = b.subscribers_only && !isSubscriber;
            const needsTicket = b.requires_ticket && b.event && b.user_has_ticket === false;
            const disabled = b.sold_out || blockedBySub || needsTicket || !affordable;
            const bannerImg = b.image_url || b.event?.image_url;
            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -6 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent overflow-hidden group hover:border-[#D4AF37]/50 hover:shadow-[0_10px_45px_rgba(212,175,55,0.12)] transition-all"
                data-testid={`points-benefit-${b.id}`}
              >
                {bannerImg && (
                  <div className="relative">
                    <img src={bannerImg} alt="" className="w-full h-32 object-cover" />
                    {b.event && (
                      <Link to={`/evento/${b.event.id}`} className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-3 pt-6 pb-2 flex items-center gap-1.5 hover:from-black" data-testid={`benefit-event-banner-${b.id}`}>
                        <Ticket size={11} className="text-[#D4AF37] flex-shrink-0" />
                        <span className="text-white text-[11px] font-bold truncate">{b.event.title}</span>
                        {b.event.date && <span className="text-white/50 text-[10px] flex-shrink-0">· {new Date(b.event.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}</span>}
                      </Link>
                    )}
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-black">
                      <Coins size={10} /> {b.points_cost.toLocaleString()} pts
                    </span>
                    {b.subscribers_only && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-[9px] font-bold uppercase"><Crown size={9} /> Solo miembros</span>
                    )}
                    {b.requires_ticket && b.event && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase ${b.user_has_ticket ? 'border-emerald-500/40 text-emerald-400' : 'border-sky-500/40 text-sky-300'}`} data-testid={`benefit-ticket-req-${b.id}`}>
                        <Ticket size={9} /> {b.user_has_ticket ? 'Tenés entrada ✓' : 'Requiere entrada'}
                      </span>
                    )}
                    {b.sold_out && <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[9px] font-bold uppercase">Agotado</span>}
                  </div>
                  <h3 className="text-white font-bold text-base mb-1">{b.title}</h3>
                  {(b.event || b.location_label) && (
                    <p className="text-sky-300/80 text-[11px] font-semibold mb-1 flex items-center gap-1" data-testid={`benefit-where-${b.id}`}>
                      📍 Canjeable en{' '}
                      {b.event ? `${b.event.title}${b.event.date ? ` · ${new Date(b.event.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}` : ''}` : b.location_label}
                      {b.event && b.location_label ? ` (${b.location_label})` : ''}
                    </p>
                  )}
                  {b.description && <p className="text-white/45 text-xs leading-relaxed mb-4 line-clamp-3">{b.description}</p>}
                  <button
                    onClick={() => redeem(b)}
                    disabled={disabled || redeeming === b.id}
                    className={`w-full px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${disabled ? 'bg-white/5 text-white/25 cursor-not-allowed' : 'bg-[#D4AF37] text-black hover:bg-[#F2D272]'}`}
                    data-testid={`redeem-btn-${b.id}`}
                  >
                    {b.sold_out ? 'Agotado' : blockedBySub ? 'Requiere FULL BRAVX' : needsTicket ? 'Requiere entrada del evento' : affordable ? (redeeming === b.id ? 'Canjeando…' : 'Canjear') : `Te faltan ${(b.points_cost - myPoints).toLocaleString()} pts`}
                  </button>
                  {needsTicket && (
                    <Link to={`/evento/${b.event.id}`} className="block text-center text-[11px] text-[#D4AF37] hover:text-[#F2D272] font-bold mt-2" data-testid={`benefit-buy-ticket-link-${b.id}`}>
                      Conseguí tu entrada →
                    </Link>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Success modal */}
      <AnimatePresence>
        {newCode && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setNewCode(null)}>
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className="bg-[#0E0E0E] border border-[#D4AF37]/50 rounded-3xl p-8 max-w-sm w-full text-center relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="redemption-success-modal"
            >
              <button onClick={() => setNewCode(null)} className="absolute top-4 right-4 text-white/40 hover:text-white"><X size={18} /></button>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 flex items-center justify-center">
                <Gift size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-black text-xl mb-1" style={{ fontFamily: "'Unbounded', sans-serif" }}>¡Canje exitoso!</h3>
              <p className="text-white/50 text-sm mb-6">{newCode.benefit_title}</p>
              <button
                onClick={() => copy(newCode.code)}
                className="w-full py-4 rounded-2xl bg-[#D4AF37]/10 border-2 border-dashed border-[#D4AF37]/60 font-mono text-2xl font-black tracking-[0.3em] text-[#D4AF37] flex items-center justify-center gap-3 hover:bg-[#D4AF37]/20"
                data-testid="redemption-code"
              >
                {newCode.code} {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
              <p className="text-white/35 text-[11px] mt-4">Guardalo — también queda en "Mis canjes" para cuando lo necesites.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
